import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Camera, Sparkles } from 'lucide-react';
import ServiceHero from '../../components/services/ServiceHero';
import { Footer } from '../../components/Footer';

const PhotoStudioPage = () => {

  const whatWeProduce = [
    { title: 'Professional headshots', desc: 'LinkedIn, website, sales page' },
    { title: 'YouTube thumbnails', desc: 'Custom colored or neutral backdrop' },
    { title: 'Online course visuals', desc: 'Module covers, presentation photos' },
    { title: 'Personal brand photos', desc: 'Social media, ads, covers' },
    { title: 'Podcast cover art', desc: 'Spotify, Apple Podcast' },
    { title: 'Digital product photos', desc: 'Course/ebook mockups' },
    { title: 'Behind the scenes (BTS)', desc: 'Stories, authentic reels' }
  ];

  const packs = [
    {
      emoji: '⚡',
      name: 'Headshot Express',
      price: 1500000,
      features: ['30 min', '3 retouched HD photos', 'Neutral backdrop', 'Delivery within 48h via Google Drive']
    },
    {
      emoji: '📸',
      name: 'Personal Brand Basic',
      price: 3500000,
      features: ['1h', '10 retouched photos', '2 different backdrops', '2 outfit changes', 'Delivery within 48h']
    },
    {
      emoji: '🌟',
      name: 'Personal Brand Pro',
      price: 6500000,
      features: ['2h', '25 retouched photos', '3 different setups (neutral + color + lifestyle)', '3 outfit changes', 'Delivery within 48h'],
      popular: true
    },
    {
      emoji: '💎',
      name: 'Brand Identity Full',
      price: 12000000,
      features: ['Half day', 'Unlimited photos', '4 different setups', 'Studio lifestyle shooting', '20 retouched HD photos selected', 'Delivery within 48h']
    },
    {
      emoji: '🎬',
      name: 'YouTube Thumbnails Pack',
      price: 2500000,
      features: ['1h', '10 retouched YouTube thumbnails ready to use', 'Custom colored backdrops', 'Text and design integrated', 'Optimized format (1280x720)', 'Delivery within 24h']
    },
    {
      emoji: '🎓',
      name: 'Course Visuals Pack',
      price: 4000000,
      features: ['1h30', '15 retouched HD photos for sales page + module covers', 'Poses adapted to your topic', 'Delivery within 48h']
    }
  ];

  const photoAddons = [
    { name: 'Extra retouch per photo', price: '+Rp 150,000' },
    { name: 'Custom colored backdrop', price: '+Rp 300,000' },
    { name: 'Hair & makeup (partner)', price: '+Rp 500,000' },
    { name: '60s presentation video filmed at same time', price: '+Rp 1,500,000' },
    { name: 'Express 24h delivery', price: '+Rp 500,000' },
    { name: 'Text overlay on photos (quote, title)', price: '+Rp 200,000/photo' }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24">
      <ServiceHero
        tag="PHOTO STUDIO"
        title="Visual Content"
        description="Everything produced directly in our studio — same lighting, same space, same quality. One place for podcast, video AND photo."
        bookLink="/services/photo-studio/book"
        heroImage="https://images.unsplash.com/photo-1758613378843-7e29e53c139a?w=1600&q=80"
        metaTitle="Photo Studio | Canggu Podcast Studio"
        metaDescription="Professional photo studio in Canggu, Bali. Headshots, thumbnails, brand photography with premium lighting and backdrops."
      />

      {/* What We Produce */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">What We Produce in the Studio</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {whatWeProduce.map((item, index) => (
              <div key={index} className="p-5 bg-[#121212] border border-[#262626] hover:border-[#F5A623]/50 transition-colors">
                <div className="flex items-start gap-3">
                  <Camera className="w-5 h-5 text-[#F5A623] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packs */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Photo Packs</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packs.map((pack, index) => (
              <div 
                key={index} 
                className={`p-6 border transition-all ${
                  pack.popular 
                    ? 'border-[#F5A623] bg-[#F5A623]/5' 
                    : 'border-[#262626] bg-[#121212] hover:border-[#F5A623]/50'
                } relative`}
              >
                {pack.popular && (
                  <span className="absolute -top-3 right-6 px-3 py-1 bg-[#F5A623] text-black text-xs font-semibold">
                    POPULAR
                  </span>
                )}
                <div className="text-3xl mb-4">{pack.emoji}</div>
                <h3 className="text-xl font-heading font-semibold text-white mb-2">{pack.name}</h3>
                <div className="text-2xl font-bold text-[#F5A623] mb-4">
                  Rp {(pack.price / 1000000).toFixed(1)}M
                </div>
                <ul className="space-y-2">
                  {pack.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <Check className="w-4 h-4 text-[#F5A623] mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Add-ons */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Photo Add-ons</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {photoAddons.map((addon, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-[#121212] border border-[#262626]">
                <span className="text-gray-300">{addon.name}</span>
                <span className="text-[#F5A623] font-semibold">{addon.price}</span>
              </div>
            ))}
          </div>
          
          {/* Combo Tip */}
          <div className="mt-8 p-6 bg-[#F5A623]/10 border border-[#F5A623]/30">
            <div className="flex items-start gap-4">
              <Sparkles className="w-6 h-6 text-[#F5A623] flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold mb-2">Combo Tip</h3>
                <p className="text-gray-400">
                  Add a photo session to any studio booking from <span className="text-[#F5A623] font-semibold">Rp 1,500,000 only</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Ready to Create Your Visual Content?
          </h2>
          <p className="text-gray-400 mb-8">
            Book your photo session and elevate your personal brand.
          </p>
          <Link
            to="/services/photo-studio/book"
            className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg hover:bg-[#FFB84D] transition-colors"
            data-testid="book-now-cta"
          >
            Book Now
          </Link>
        </div>
      </section>

      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PhotoStudioPage;
